import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useSearchParams } from 'react-router-dom'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'

interface AnafSettings {
  cif: string | null
  tokensSet: boolean
  enabled: boolean
  expiresAt: string | null
  lastRefreshAt: string | null
  lastListSyncAt: string | null
  lastListSyncCount: number | null
  updatedAt: string
}

interface SettingsResp {
  settings: AnafSettings | null
  clientConfigured: boolean
}

export function SetariAnafPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('anaf:write')
  const [searchParams, setSearchParams] = useSearchParams()

  const [cif, setCif] = useState('')
  const [accessToken, setAccessToken] = useState('')
  const [refreshToken, setRefreshToken] = useState('')
  const [connectCif, setConnectCif] = useState('')

  // Procesare callback OAuth
  useEffect(() => {
    if (searchParams.get('connected') === '1') {
      toast.success('Conectare ANAF reușită — accesul a fost salvat')
      queryClient.invalidateQueries({ queryKey: ['anaf-settings'] })
      const next = new URLSearchParams(searchParams)
      next.delete('connected')
      setSearchParams(next, { replace: true })
    }
    const err = searchParams.get('anaf_error')
    if (err) {
      toast.error(`Eroare conectare ANAF: ${err}`)
      const next = new URLSearchParams(searchParams)
      next.delete('anaf_error')
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const query = useQuery<SettingsResp>({
    queryKey: ['anaf-settings'],
    queryFn: async () => (await api.get<SettingsResp>('/anaf/settings')).data,
    refetchInterval: 60_000,
  })

  const saveMutation = useMutation({
    mutationFn: async () =>
      (
        await api.put('/anaf/settings', {
          cif: cif.trim().toUpperCase(),
          accessToken: accessToken.trim(),
          refreshToken: refreshToken.trim(),
          expiresIn: 90 * 24 * 60 * 60,
        })
      ).data,
    onSuccess: () => {
      toast.success('Acces salvat. Sincronizarea mesajelor SPV pornește automat în câteva minute.')
      queryClient.invalidateQueries({ queryKey: ['anaf-settings'] })
      setAccessToken('')
      setRefreshToken('')
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare la salvare'
      toast.error(message)
    },
  })

  const [histStart, setHistStart] = useState('2024-01-01')
  const [histEnd, setHistEnd] = useState(new Date().toISOString().slice(0, 10))

  const historicalMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post<{
          ok: boolean
          newMessages?: number
          totalChecked?: number
          pages?: number
          error?: { message: string }
        }>('/anaf/sync-historical', {
          startDate: histStart,
          endDate: histEnd,
        })
      ).data,
    onSuccess: (d) => {
      if (d.ok) {
        toast.success(
          `Istoric: ${d.newMessages ?? 0} noi din ${d.totalChecked ?? 0} verificate (${d.pages ?? 0} pagini)`,
        )
      } else {
        toast.error(d.error?.message ?? 'Sincronizarea istoricului a eșuat')
      }
    },
    onError: () => toast.error('Sincronizarea istoricului a eșuat'),
  })

  const refreshMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ ok: boolean; expiresAt: string; expiresInDays: number }>(
          '/anaf/refresh-now',
        )
      ).data,
    onSuccess: (d) => {
      toast.success(`Token reînnoit (valabil ${d.expiresInDays} zile)`)
      queryClient.invalidateQueries({ queryKey: ['anaf-settings'] })
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Accesul nu s-a putut reînnoi. Încearcă din nou.'
      toast.error(message)
    },
  })

  const [debugResult, setDebugResult] = useState<unknown>(null)
  const debugMutation = useMutation({
    mutationFn: async () => (await api.get('/anaf/debug/list?days=30')).data,
    onSuccess: (d) => {
      setDebugResult(d)
      toast.success('Debug ok — vezi JSON-ul mai jos')
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Diagnosticul a eșuat'
      toast.error(message)
    },
  })

  const connectMutation = useMutation({
    mutationFn: async (cifToUse: string) =>
      (
        await api.post<{ authorizeUrl: string; state: string }>('/anaf/oauth/start', {
          cif: cifToUse,
        })
      ).data,
    onSuccess: (d) => {
      // Deschidem URL-ul ANAF in tab nou — utilizatorul autentifica cu eToken
      window.open(d.authorizeUrl, '_blank', 'noopener,noreferrer')
      toast.info('Pagina ANAF s-a deschis într-un tab nou. Autentifică-te cu eToken-ul.')
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Conectarea nu a putut porni'
      toast.error(message)
    },
  })

  const s = query.data?.settings
  const expiresAt = s?.expiresAt ? new Date(s.expiresAt) : null
  const daysLeft = expiresAt
    ? Math.floor((expiresAt.getTime() - Date.now()) / (24 * 60 * 60 * 1000))
    : null
  const lastSync = s?.lastListSyncAt ? new Date(s.lastListSyncAt) : null
  const lastRefresh = s?.lastRefreshAt ? new Date(s.lastRefreshAt) : null

  const expiryColor =
    daysLeft === null
      ? 'text-b4l-text-muted'
      : daysLeft < 7
        ? 'text-b4l-red-dark'
        : daysLeft < 14
          ? 'text-b4l-amber-dark'
          : 'text-b4l-green-dark'

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Setări → ANAF eFactura</h1>

      {!query.data?.clientConfigured && (
        <Card>
          <div className="rounded border border-b4l-amber-light bg-b4l-amber-light/30 p-3 text-xs">
            ⚠ <strong>ANAF_CLIENT_ID</strong> și <strong>ANAF_CLIENT_SECRET</strong> nu sunt
            configurate în <code>.env</code> pe server. Reînnoirea automată a accesului nu
            funcționează până nu sunt completate. Le obții din contul ANAF, la înregistrarea
            aplicației (
            <a
              href="https://logincert.anaf.ro/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              logincert.anaf.ro
            </a>
            ).
          </div>
        </Card>
      )}

      <Card title="Starea conexiunii">
        {s && s.tokensSet ? (
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-b4l-text-muted">CIF:</span>
              <span className="font-mono font-medium">{s.cif}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-b4l-text-muted">Tokeni:</span>
              <span className="text-b4l-green-dark">✓ configurate</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-b4l-text-muted">Expirare access_token:</span>
              <span className={`font-medium ${expiryColor}`}>
                {expiresAt ? expiresAt.toLocaleString('en-GB') : '—'}
                {daysLeft !== null && (
                  <span className="ml-2">
                    ({daysLeft >= 0 ? `${daysLeft} zile rămase` : 'EXPIRAT'})
                  </span>
                )}
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs text-b4l-text-muted">
              <span>Ultima reînnoire:</span>
              <span>{lastRefresh ? lastRefresh.toLocaleString('en-GB') : 'niciodată'}</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-b4l-text-muted">
              <span>Ultima sincronizare a mesajelor:</span>
              <span>
                {lastSync ? lastSync.toLocaleString('en-GB') : 'niciodată'}
                {s.lastListSyncCount !== null && ` (${s.lastListSyncCount} mesaje noi)`}
              </span>
            </div>

            {canWrite && (
              <div className="mt-3 flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => refreshMutation.mutate()}
                  loading={refreshMutation.isPending}
                >
                  Reînnoiește token acum
                </Button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-b4l-text-muted">
            Accesul la ANAF nu e configurat. Conectează-te mai jos, cu eToken.
          </p>
        )}
      </Card>

      {query.data?.clientConfigured && (
        <Card
          title="Conectare ANAF (recomandat)"
          description="Apasă butonul: se deschide pagina ANAF, te autentifici cu certificatul digital (eToken), iar accesul se salvează automat în CRM. Nu trebuie să generezi nimic manual."
        >
          <div className="space-y-3">
            <Field
              label="CIF Boost4Life (cu prefix RO)"
              placeholder="RO12345678"
              value={connectCif}
              onChange={(e) => setConnectCif(e.target.value)}
              disabled={!canWrite}
              hint="Trebuie să fie CIF-ul firmei pentru care ești autorizat în SPV (titularul certificatului digital)."
            />
            {canWrite && (
              <Button
                onClick={() => connectMutation.mutate(connectCif.trim().toUpperCase())}
                loading={connectMutation.isPending}
                disabled={!connectCif.trim()}
              >
                Conectează-te la ANAF cu eToken →
              </Button>
            )}
            <div className="rounded bg-b4l-blue-light/30 p-3 text-xs text-b4l-text-dim">
              <p className="mb-1">
                <strong>Asigură-te că:</strong>
              </p>
              <ul className="ml-4 list-disc space-y-0.5">
                <li>Stickul USB cu certificatul digital ANAF e conectat</li>
                <li>Browserul are certificatul instalat (sau va apărea o fereastră la clic)</li>
                <li>Ești delegat în SPV pentru CIF-ul completat</li>
              </ul>
            </div>
          </div>
        </Card>
      )}

      <Card
        title="Configurare manuală a tokenilor (variantă de rezervă)"
        description="Dacă preferi să generezi tokenii separat (de ex. cu un script propriu) și să-i lipești aici. Folosește varianta asta doar dacă conectarea automată de mai sus nu funcționează."
      >
        <div className="space-y-3">
          <Field
            label="CIF Boost4Life (cu prefix RO)"
            placeholder="RO12345678"
            value={cif}
            onChange={(e) => setCif(e.target.value)}
            disabled={!canWrite}
          />
          <div>
            <label className="block text-xs font-medium text-b4l-text-dim">
              access_token (90 zile)
            </label>
            <textarea
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              disabled={!canWrite}
              rows={3}
              placeholder="eyJ..."
              className="mt-1 w-full rounded border border-b4l-border bg-white px-2 py-1.5 font-mono text-xs"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-b4l-text-dim">
              refresh_token (90 zile)
            </label>
            <textarea
              value={refreshToken}
              onChange={(e) => setRefreshToken(e.target.value)}
              disabled={!canWrite}
              rows={3}
              placeholder="eyJ..."
              className="mt-1 w-full rounded border border-b4l-border bg-white px-2 py-1.5 font-mono text-xs"
            />
          </div>

          {canWrite && (
            <Button
              onClick={() => saveMutation.mutate()}
              loading={saveMutation.isPending}
              disabled={!cif.trim() || !accessToken.trim() || !refreshToken.trim()}
            >
              Salvează
            </Button>
          )}
        </div>
      </Card>

      {s && s.tokensSet && canWrite && (
        <Card
          title="Diagnostic ANAF"
          description="Apelează ANAF direct și arată răspunsul brut — util ca să vezi exact ce returnează ANAF (cod de eroare, CIF etc.)."
        >
          <Button
            variant="secondary"
            onClick={() => debugMutation.mutate()}
            loading={debugMutation.isPending}
          >
            Interoghează ANAF (ultimele 180 de zile)
          </Button>
          {debugResult ? (
            <pre className="mt-3 max-h-96 overflow-auto rounded border border-b4l-border bg-b4l-bg-soft p-3 font-mono text-[11px]">
              {JSON.stringify(debugResult, null, 2)}
            </pre>
          ) : null}
        </Card>
      )}

      {s && s.tokensSet && (
        <Card
          title="Sincronizare istoric"
          description="Trage facturile din SPV pe un interval de date. Folosește pentru backfill inițial (de la începutul eFactura - sau o dată mai recentă)."
        >
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <Field
              label="De la"
              type="date"
              value={histStart}
              onChange={(e) => setHistStart(e.target.value)}
            />
            <Field
              label="Până la"
              type="date"
              value={histEnd}
              onChange={(e) => setHistEnd(e.target.value)}
            />
            <div className="flex items-end">
              {canWrite && (
                <Button
                  variant="secondary"
                  onClick={() => historicalMutation.mutate()}
                  loading={historicalMutation.isPending}
                >
                  Sincronizează istoricul
                </Button>
              )}
            </div>
          </div>
          <p className="mt-2 text-xs text-b4l-text-muted">
            ⏱ Pentru ~3 ani de date poate dura ~5-10 minute. ANAF returnează maxim 500 mesaje per
            pagină. După listare, descărcarea XML-urilor se face automat de cron-ul de 3 minute.
          </p>
        </Card>
      )}

      <Card description="Cum funcționează: CRM-ul reînnoiește automat accesul, săptămânal. Nu trebuie să intervii decât dacă serverul a stat oprit peste 90 de zile: atunci vezi o alertă și te reconectezi cu eToken-ul (butonul de mai sus).">
        <p className="text-xs text-b4l-text-muted">
          <strong>Notă:</strong> accesul ANAF e folosit doar pentru citire (listare și descărcare
          mesaje SPV). CRM-ul nu emite și nu modifică nimic în SPV.
        </p>
      </Card>
    </div>
  )
}
